import { Remarkable } from 'remarkable';

const md = new Remarkable({
    html: false, // Enable HTML tags in source
    xhtmlOut: false, // Use '/' to close single tags (<br />)
    breaks: false, // Convert '\n' in paragraphs into <br>
    langPrefix: 'language-', // CSS language prefix for fenced blocks

    // Enable some language-neutral replacement + quotes beautification
    typographer: false,

    // Double + single quotes replacement pairs, when typographer enabled,
    // and smartquotes on. Set doubles to '«»' for Russian, '„“' for German.
    quotes: '“”‘’',

    // Highlighter function. Should return escaped HTML,
    // or '' if the source string is not changed
    highlight(/* str, lang */) {
        return '';
    },
});

export const markdownToHtml = (markdown: string): string => {
    let html = md.render(markdown);

    // Remove paragraph around..
    if (html.startsWith('<p>')) {
        html = html.substring(3, html.length - 5);
    }

    console.log('markdowntoHTML', html);

    return html;
};

export function isEmpty(str: string | undefined): boolean {
    return str === undefined || str === null || str.trim().length === 0;
}

/**
 * Splits a string on semicolons ignoring any whitespace adjacent to the semicolons.
 * Slightly more human-proof than .split(';').  If string is empty returns [] which
 * is different than .split which returns [""].
 *
 * @param str
 * @returns
 */
export function splitOnSemicolon(str: string) {
    if (!str) return [];
    return str.split(/\s*;\s*/);
}

/**
 * @param str - The string to sanitize
 * @returns {string} - The sanitized string
 */
export const escapeForRegExp = (str: string): string =>
    str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const cleanExtraSpaces = (str: string): string =>
    str === null || str === undefined ? '' : str.replace(/\s+/g, ' ').trim();

export function simplifyName(
    name: string | undefined,
    hideName: boolean
): string | undefined {
    if (!name) {
        return name;
    }

    // Morten Moeller => M. M.
    if (hideName) {
        return (
            name
                .trim()
                .toUpperCase()
                .split(' ')
                .filter((n) => n.trim().length > 0)
                .map((n) => n[0])
                .join('. ') + '.'
        );
    }

    // Morten Moeller => Morten M.
    return name
        .trim()
        .split(' ')
        .map((piece, index) =>
            index === 0
                ? piece
                : piece.length === 0
                ? undefined
                : piece.substring(0, 1).toUpperCase() + '.'
        )
        .filter((p) => !!p)
        .join(' ');
}

/**
 * Enhanced trim function that removes hidden characters and trims whitespace from start and end
 *
 * Removes zero-width characters throughout the string and trims whitespace, newlines, tabs,
 * and other control characters from the beginning and end of the string. Unlike regular trim(),
 * this handles newlines, tabs, and hidden Unicode characters.
 *
 * @param str - The input string to clean
 * @returns A cleaned string with hidden characters removed and whitespace trimmed from ends
 */
export function trim(str: string): string {
    return (
        str
            // Remove zero-width characters and other hidden Unicode characters throughout
            .replaceAll(/[\u200B-\u200D\u2060\uFEFF]/g, '')
            // Convert non-breaking spaces to regular spaces
            .replaceAll(/\u00A0/g, ' ')
            // Trim whitespace, newlines, tabs, and control characters from start and end
            .replace(/^[\s\r\n\t\f\v]+|[\s\r\n\t\f\v]+$/g, '')
    );
}

/**
 * Converts a normal email address into a sandboxed test address.
 *
 * Transforms:
 *   - '@' → '_at_'
 *   - '.' → '_dot_'
 * Then prepends "devsandbox+" and appends "@realworklabs.com".
 *
 * @param {string} recipient - The original email address (e.g. "FirstName LastName <test@domain.com>" or "test@domain.com").
 * @returns {string | undefined} The transformed sandbox email (e.g. "FirstName LastName <devsandbox+test_at_domain_dot_com@realworklabs.com>" or "devsandbox+test_at_domain_dot_com@realworklabs.com").
 */
export function obfuscateEmail(recipient?: string): string | undefined {
    if (!recipient || !recipient.trim() || !trim(recipient)) {
        return undefined;
    }

    const PREFIX = 'devsandbox+';
    const SUFFIX = '@realworklabs.com';

    let namePart = '';
    let emailPart = trim(recipient);

    // If there's a name + angle‑bracketed email, split them
    const match = emailPart.match(/^(.*)<([^>]+)>$/);
    if (match) {
        namePart = match[1].trim();
        emailPart = match[2].trim();
    }

    if (!emailPart || !emailPart.trim()) {
        return undefined; // No valid email part found
    }

    const sanitized = emailPart
        .replaceAll(/@/g, '_at_')
        .replaceAll(/\./g, '_dot_');

    const obfuscated = `${PREFIX}${sanitized}${SUFFIX}`;

    return namePart ? `${namePart} <${obfuscated}>` : obfuscated;
}
